import { Component, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, tap } from 'rxjs/operators';
import { DateRangePreset, FilterBarComponent } from '../shared/filter-bar/filter-bar.component';
import { VinInputComponent } from './vin-input/vin-input.component';
import { VinEntry, VinKind } from './vin-entry';
import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import { DataService } from '../services/data.service';
import { SettingsService, utcOffsetValue } from '../services/settings.service';
import { EMPTY_VIN_LOOKUP, VinLookupData, VinLookupResult } from '../models/vinLookup/vinLookupResponse.model';
import { latestLoad } from '../shared/latest-load';

type DateRange = { start: Date; end: Date };

/** A submitted search — what the results on screen were asked for. */
interface VinSearch {
  /** As classified when entered; every one is full or partial. */
  vins: VinEntry[];
  dateRange: DateRange;
}

/** One lookup request: the search, run in a given time zone. */
interface LookupLoad {
  search: VinSearch;
  timezone: string;
}

/** Emailing is the bulk path; one certificate opens from its row's link. */
const MIN_BULK_SELECTION = 2;

/**
 * Download Certificate: looks up processed vehicles by VIN (full or partial)
 * within a date range. Unlike the reports nothing loads on arrival — the user
 * composes a search in the filter bar and runs it, and the results replace the
 * bar until they choose to edit the search.
 *
 * Data filters don't scope this page; the VINs are the filter.
 */
@Component({
  selector: 'app-vin-lookup',
  imports: [
    FilterBarComponent,
    VinInputComponent,
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatToolbarModule,
    MatProgressBarModule,
    MatCheckboxModule,
    MatTooltipModule
  ],
  templateUrl: './vin-lookup.component.html',
  styleUrl: './vin-lookup.component.scss'
})
export class VinLookupComponent {
  readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private readonly data = inject(DataService);
  private readonly settings = inject(SettingsService);
  private readonly snackBar = inject(MatSnackBar);

  readonly maxRangeMonths = 12;
  readonly presets: DateRangePreset[] = ['today', 'last7', 'last14', 'last30'];
  readonly minBulkSelection = MIN_BULK_SELECTION;

  readonly displayedColumns = [
    'select',
    'timestamp',
    'status',
    'vinCode',
    'decodedMake',
    'decodedModel',
    'decodedYear',
    'processorSite',
    'userName',
    'emailID'
  ];

  /** The VINs being composed. Kept through "Edit search"; gone with the page. */
  readonly draft = signal<VinEntry[]>([]);

  /** The submitted search, or null while one is being composed — results show only once set. */
  readonly search = signal<VinSearch | null>(null);

  readonly results = signal<VinLookupResult[]>([]);
  readonly loading = signal(false);

  /** Searched values the lookup matched nothing for — full and partial alike. */
  readonly notFound = signal<ReadonlySet<string>>(new Set());

  readonly dataSource = new MatTableDataSource<VinLookupResult>([]);
  private readonly paginator = viewChild(MatPaginator);

  /** Something to search for, nothing invalid among it, and a complete range. */
  readonly canSearch = computed(() => {
    const draft = this.draft();
    return draft.length > 0
      && draft.every(entry => entry.kind !== null)
      && this.data.dateRange() !== null;
  });

  /** Rows with a certificate — the only ones that can be selected. */
  readonly selectable = computed(() => this.results().filter(row => row.certificateLink));

  /** Selected scans by history id. Spans every page of results, not just the one in view. */
  readonly selectedIds = signal<ReadonlySet<string>>(new Set());

  readonly allSelected = computed(() => {
    const selectable = this.selectable();
    const selected = this.selectedIds();
    return selectable.length > 0 && selectable.every(row => selected.has(row.scanHistoryId));
  });

  readonly someSelected = computed(() => this.selectedIds().size > 0 && !this.allSelected());

  /** An email request is in flight — blocks a second send of the same selection. */
  readonly sending = signal(false);

  readonly canEmail = computed(() => this.selectedIds().size >= MIN_BULK_SELECTION && !this.sending());

  /** Only the latest lookup may land — see latestLoad. */
  private readonly lookup = latestLoad<LookupLoad>(
    ({ search, timezone }) => {
      this.setResults(EMPTY_VIN_LOOKUP);
      return this.api.getVinLookup(
        valuesOf(search.vins, 'full'),
        valuesOf(search.vins, 'partial'),
        toIsoDate(search.dateRange.start),
        toIsoDate(search.dateRange.end),
        utcOffsetValue(timezone, search.dateRange.start)
      ).pipe(tap(lookup => this.setResults(lookup)));
    },
    active => this.loading.set(active)
  );

  constructor() {
    // Runs each submitted search, and re-runs it on a time zone change — the
    // offset decides where the range's days start and end. The date range is
    // deliberately not read here: picking dates never searches by itself.
    effect(() => {
      const search = this.search();
      const timezone = this.settings.timezone();
      if (search) {
        untracked(() => this.lookup({ search, timezone }));
      }
    });

    // The paginator only exists while results are showing.
    effect(() => this.dataSource.paginator = this.paginator() ?? null);
  }

  /** Snapshot the VINs and range; the effect above runs it. */
  runSearch(): void {
    const dateRange = this.data.dateRange();
    if (!this.canSearch() || !dateRange) {
      return;
    }
    this.search.set({ vins: this.draft(), dateRange });
  }

  /** Back to the filter bar, VINs and dates as they were. */
  editSearch(): void {
    this.search.set(null);
  }

  clear(): void {
    this.draft.set([]);
  }

  /** Dropping a VIN from the summary narrows the search; dropping the last returns to composing one. */
  removeFromSearch(vin: string): void {
    const search = this.search();
    if (!search) {
      return;
    }
    const vins = search.vins.filter(entry => entry.value !== vin);
    this.draft.update(entries => entries.filter(entry => entry.value !== vin));
    this.search.set(vins.length ? { ...search, vins } : null);
  }

  isSelected(row: VinLookupResult): boolean {
    return row.certificateLink != null && this.selectedIds().has(row.scanHistoryId);
  }

  toggle(row: VinLookupResult): void {
    if (!row.certificateLink) {
      return;
    }
    this.selectedIds.update(ids => {
      const next = new Set(ids);
      if (!next.delete(row.scanHistoryId)) {
        next.add(row.scanHistoryId);
      }
      return next;
    });
  }

  /** Header checkbox: every certificate in the results, or none. */
  toggleAll(): void {
    this.selectedIds.set(this.allSelected()
      ? new Set()
      : new Set(this.selectable().map(row => row.scanHistoryId)));
  }

  /**
   * Emails the selected certificates over the searched range — the range the
   * results came from, in the current time zone, as the lookup sent it. The
   * server's message goes straight to the snackbar.
   */
  emailCertificates(): void {
    const search = this.search();
    if (!this.canEmail() || !search) {
      return;
    }
    const selected = this.selectedIds();
    const certificates = this.selectable()
      .filter(row => selected.has(row.scanHistoryId))
      .map(row => ({ vinCode: row.vinCode, scanHistoryId: row.scanHistoryId }));
    const { start, end } = search.dateRange;

    this.sending.set(true);
    this.api.emailCertificates(
      certificates,
      toIsoDate(start),
      toIsoDate(end),
      utcOffsetValue(this.settings.timezone(), start)
    )
      .pipe(finalize(() => this.sending.set(false)))
      .subscribe(response => {
        this.snackBar.open(response.message || 'Certificate request submitted.', 'Dismiss', { duration: 5000 });
        if (response.success) {
          // Cleared so a second click can't resend the same batch.
          this.selectedIds.set(new Set());
        }
      });
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat(this.settings.locale(), { dateStyle: 'short' }).format(date);
  }

  formatDateTime(iso: string): string {
    return new Intl.DateTimeFormat(this.settings.locale(), {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: this.settings.timezone()
    }).format(new Date(iso));
  }

  private setResults({ results, notFoundVins, notFoundPartialVins }: VinLookupData): void {
    this.results.set(results);
    this.dataSource.data = results;
    // Upper-cased to match the searched values, which are normalised the same way.
    this.notFound.set(new Set([...notFoundVins, ...notFoundPartialVins].map(vin => vin.toUpperCase())));
    // A selection only means something against the results it was made in.
    this.selectedIds.set(new Set());
  }
}

function valuesOf(entries: VinEntry[], kind: VinKind): string[] {
  return entries.filter(entry => entry.kind === kind).map(entry => entry.value);
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
